import React, { useState, useEffect, useRef } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { PlayIcon } from "@heroicons/react/24/outline";
import { useVideoPreferences } from "../../../../contexts/VideoPreferencesContext";
import { useNotification } from "../../../../hooks/useNotification";
import Button from "../../../../componentLibrary/Button";
import { analytics } from "../../../../services/AnalyticsService";
import { AnalyticsCategoryEnum, LessonI } from "../../../../types";

export default function VideoComponent({
  url,
  title,
  transcript,
  lesson,
  moduleId,
  pageIndex,
  pageId,
  fitInContainer = false,
}: {
  url: string;
  title?: string;
  transcript?: string;
  lesson?: LessonI;
  moduleId?: string;
  pageIndex?: number;
  pageId?: string;
  fitInContainer?: boolean;
}) {
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const hasTrackedView = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const volumeSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAppliedInitialVolume = useRef(false); // Only apply stored volume ONCE
  const hasAppliedLoadedVolume = useRef(false); // Track if we applied after preferences loaded

  const {
    videoPlaybackSpeed,
    setVideoPlaybackSpeed,
    autoplayVideos,
    setAutoplayVideos,
    videoVolume,
    setVideoVolume,
    isUpdatingAutoplay,
    isUpdatingSpeed,
    isLoading: isLoadingPreferences,
  } = useVideoPreferences();
  const [isIframeReady, setIsIframeReady] = useState(false);
  const { notify } = useNotification();
  const [localSpeedUpdate, setLocalSpeedUpdate] = useState(false);

  // Store latest callbacks in refs to avoid recreating effects when they change
  const setVideoVolumeRef = useRef(setVideoVolume);
  const videoVolumeRef = useRef(videoVolume);

  // Update refs when values change
  useEffect(() => {
    setVideoVolumeRef.current = setVideoVolume;
    videoVolumeRef.current = videoVolume;
  }, [setVideoVolume, videoVolume]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (volumeSaveTimeoutRef.current) {
        clearTimeout(volumeSaveTimeoutRef.current);
      }
    };
  }, []);

  // Handler to toggle autoplay preference
  const handleToggleAutoplay = async () => {
    try {
      await setAutoplayVideos(!autoplayVideos);
      notify(
        "success",
        `Autoplay ${!autoplayVideos ? "enabled" : "disabled"} for all videos`,
      );
    } catch (error) {
      notify("error", "Failed to update autoplay preference");
    }
  };

  const toggleTranscript = () => {
    setTranscriptVisible(!transcriptVisible);
  };

  // Track video view when video is played
  const handleVideoPlay = () => {
    if (!hasTrackedView.current && url && lesson?._id && moduleId) {
      analytics.track("videoView", {
        category: AnalyticsCategoryEnum.Video,
        additionalDetails: {
          videoUrl: url,
          lessonId: lesson._id,
          moduleId: moduleId,
          pageIndex: pageIndex ?? 0,
          pageId: pageId,
        },
      });
      hasTrackedView.current = true;
    }
  };

  // Parse video URL to enable Vimeo API for tracking, set playback speed and autoplay
  const getVideoUrlWithTracking = (videoUrl: string) => {
    try {
      const url = new URL(videoUrl);
      // Add API parameter for Vimeo to enable postMessage events
      if (url.hostname.includes("vimeo.com")) {
        // Check if api parameter already exists
        if (!url.searchParams.has("api")) {
          url.searchParams.set("api", "1");
        }
        // Add speed parameter to set default playback speed
        url.searchParams.set("speed", "1"); // Enable speed controls
        // Add autoplay parameter if user preference is enabled
        if (autoplayVideos) {
          url.searchParams.set("autoplay", "1");
        }
        return url.toString();
      }
      return videoUrl;
    } catch {
      return videoUrl;
    }
  };

  // Reset tracking and iframe ready state when video URL changes
  useEffect(() => {
    hasTrackedView.current = false;
    setIsIframeReady(false);
    hasAppliedInitialVolume.current = false; // Reset for new video
    hasAppliedLoadedVolume.current = false;
  }, [url]);

  /**
   * Apply initial volume to Vimeo iframe - only called once per video
   */
  const applyInitialVolume = () => {
    if (hasAppliedInitialVolume.current) return; // Already applied

    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    hasAppliedInitialVolume.current = true;

    // Mark if preferences were already loaded when we applied
    if (!isLoadingPreferences) {
      hasAppliedLoadedVolume.current = true;
    }

    const volumeMessage = JSON.stringify({
      method: "setVolume",
      value: videoVolumeRef.current,
    });
    iframe.contentWindow.postMessage(volumeMessage, "*");
  };

  /**
   * Handle volume change events from Vimeo - save user's volume preference
   */
  const handleVolumeChange = (newVolume: number) => {
    // Ignore tiny changes (noise from Vimeo)
    if (Math.abs(newVolume - videoVolumeRef.current) < 0.01) {
      return;
    }

    // Clear any existing timeout
    if (volumeSaveTimeoutRef.current) {
      clearTimeout(volumeSaveTimeoutRef.current);
    }

    // Save after short delay (handles both clicks and scroll end)
    volumeSaveTimeoutRef.current = setTimeout(() => {
      setVideoVolumeRef.current(newVolume).catch(() => {
        notify("error", "Failed to save volume preference");
      });
    }, 300);
  };

  /**
   * Handle playback rate change events from Vimeo
   */
  const handlePlaybackRateChange = (newSpeed: number) => {
    if (newSpeed !== videoPlaybackSpeed && newSpeed >= 0.25 && newSpeed <= 2) {
      setLocalSpeedUpdate(true);
      setVideoPlaybackSpeed(newSpeed)
        .then(() => {
          notify("success", `Video speed updated to ${newSpeed}x`);
        })
        .catch(() => {
          notify("error", "Failed to save video speed preference");
        })
        .finally(() => {
          setLocalSpeedUpdate(false);
        });
    }
  };

  /**
   * Handle Vimeo ready/loaded events
   */
  const handleVimeoReady = () => {
    setIsIframeReady(true);
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow && url.includes("vimeo.com")) {
      // Apply speed
      const speedMessage = JSON.stringify({
        method: "setPlaybackRate",
        value: videoPlaybackSpeed,
      });
      iframe.contentWindow.postMessage(speedMessage, "*");

      // Apply initial volume only once
      applyInitialVolume();
    }
  };

  // Listen for Vimeo events
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data && typeof event.data === "string") {
        try {
          const data = JSON.parse(event.data);
          if (data.event === "play") {
            handleVideoPlay();
          } else if (data.event === "ready" || data.event === "loaded") {
            handleVimeoReady();
          } else if (
            data.event === "playbackratechange" &&
            data.data?.playbackRate
          ) {
            handlePlaybackRateChange(data.data.playbackRate);
          } else if (
            data.event === "volumechange" &&
            data.data?.volume !== undefined
          ) {
            handleVolumeChange(data.data.volume);
          }
        } catch {
          // Not a JSON message, ignore
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Setup Vimeo event listeners
    const setupVimeoListener = () => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow && url.includes("vimeo.com")) {
        const events = [
          "ready",
          "loaded",
          "play",
          "pause",
          "playbackratechange",
          "volumechange",
        ];
        events.forEach((eventName) => {
          const message = JSON.stringify({
            method: "addEventListener",
            value: eventName,
          });
          iframe.contentWindow!.postMessage(message, "*");
        });

        // Set playback speed immediately
        const speedMessage = JSON.stringify({
          method: "setPlaybackRate",
          value: videoPlaybackSpeed,
        });
        iframe.contentWindow.postMessage(speedMessage, "*");

        // Apply initial volume only once
        applyInitialVolume();
      }
    };

    // Setup listener after iframe loads
    const timeoutId = setTimeout(setupVimeoListener, 1000);

    return () => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow && url.includes("vimeo.com")) {
        const events = [
          "ready",
          "loaded",
          "play",
          "pause",
          "playbackratechange",
          "volumechange",
        ];
        events.forEach((eventName) => {
          const message = JSON.stringify({
            method: "removeEventListener",
            value: eventName,
          });
          iframe.contentWindow!.postMessage(message, "*");
        });
      }

      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
    };
  }, [
    url,
    lesson,
    moduleId,
    pageIndex,
    pageId,
    videoPlaybackSpeed,
    setVideoPlaybackSpeed,
  ]);

  // Apply playback speed when iframe is ready or speed changes
  useEffect(() => {
    if (isIframeReady) {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow && url.includes("vimeo.com")) {
        const timeoutId = setTimeout(() => {
          const message = JSON.stringify({
            method: "setPlaybackRate",
            value: videoPlaybackSpeed,
          });
          iframe.contentWindow!.postMessage(message, "*");
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isIframeReady, videoPlaybackSpeed, url]);

  // Fix: Apply correct volume when preferences load late (runs only once)
  useEffect(() => {
    if (
      isIframeReady &&
      !isLoadingPreferences &&
      hasAppliedInitialVolume.current &&
      !hasAppliedLoadedVolume.current
    ) {
      hasAppliedLoadedVolume.current = true;

      const iframe = iframeRef.current;
      if (iframe?.contentWindow && url.includes("vimeo.com")) {
        const volumeMessage = JSON.stringify({
          method: "setVolume",
          value: videoVolumeRef.current,
        });
        iframe.contentWindow.postMessage(volumeMessage, "*");
      }
    }
  }, [isIframeReady, isLoadingPreferences, url]);

  const videoBox = (
    <div
      className={
        fitInContainer
          ? "w-full max-h-[55vh] aspect-video relative rounded-md overflow-hidden"
          : "relative w-full rounded-md overflow-hidden"
      }
      style={fitInContainer ? undefined : { paddingBottom: "56.25%" }}
    >
      <iframe
        ref={iframeRef}
        className="absolute top-0 left-0 w-full h-full"
        src={getVideoUrlWithTracking(url)}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
      {(isUpdatingSpeed || localSpeedUpdate) && (
        <div className="absolute top-4 right-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded-md flex items-center gap-2 z-10">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          <span className="text-sm">Updating speed...</span>
        </div>
      )}
    </div>
  );

  return (
    <div className={fitInContainer ? "w-full flex flex-col" : "w-full"}>
      {url && (
        <>
          {fitInContainer ? (
            <div className="flex justify-center">{videoBox}</div>
          ) : (
            videoBox
          )}
          <div className="mt-2 flex items-center justify-between px-1 sm:px-2 gap-2 sm:gap-4">
            {transcript && (
              <button onClick={toggleTranscript} className="flex items-center">
                {transcriptVisible ? "Hide Transcript" : "Show Transcript"}
                <span
                  className={`ml-2 transform ${transcriptVisible ? "rotate-90" : "rotate-0"}`}
                  style={{ transition: "transform 0.2s" }}
                >
                  <ChevronRightIcon className="h-4 w-4 text-blue-500" />
                </span>
              </button>
            )}
            <Button
              onClick={handleToggleAutoplay}
              variant={autoplayVideos ? "primary" : "secondary"}
              size="sm"
              className="gap-1 sm:gap-2 !whitespace-nowrap min-h-[2rem] py-1.5 px-2 sm:px-4 flex-shrink-0"
              title={autoplayVideos ? "Autoplay is ON" : "Autoplay is OFF"}
              loading={isUpdatingAutoplay}
              disabled={isUpdatingAutoplay}
            >
              <PlayIcon className="h-4 w-4 flex-shrink-0" />
              Autoplay: {autoplayVideos ? "ON" : "OFF"}
            </Button>
          </div>
        </>
      )}
      <div className="px-2 sm:px-4">
        <h3>{title}</h3>
        {transcript && transcriptVisible && (
          <div
            dangerouslySetInnerHTML={{ __html: transcript }}
            className="ql-editor transcript-dropdown mt-2 p-2 sm:p-4 border border-black-300 shadow-lg bg-white max-h-60 overflow-y-auto rounded-md"
          />
        )}
      </div>
    </div>
  );
}
